/**
 * ADR-0016 — Business → Store catalog (spatial dimension for inventory ledger).
 * Soft default: single store `store_default` until Multi-Store UI grows.
 */

/** Keep in sync: canonical export; re-exported from movements for stable API */
export const DEFAULT_STORE_ID = 'store_default';

/**
 * @typedef {object} BusinessStore
 * @property {string} id
 * @property {string} name
 * @property {boolean} [isDefault]
 * @property {boolean} [active]
 */

/**
 * @param {object} [parsed]
 * @returns {BusinessStore[]}
 */
export function listBusinessStores(parsed) {
  const raw = Array.isArray(parsed?.stores) ? parsed.stores : [];
  const out = [];
  const seen = new Set();
  for (const s of raw) {
    if (!s) continue;
    const id = String(s.id ?? s.storeId ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name: String(s.name || s.label || id),
      isDefault: s.isDefault === true || id === String(parsed?.defaultStoreId || ''),
      active: s.active !== false
    });
  }
  return out;
}

/**
 * Ensure at least one default store; mutate parsed when needed.
 * @param {object} parsed
 * @returns {BusinessStore[]}
 */
export function ensureBusinessStores(parsed) {
  if (!parsed || typeof parsed !== 'object') return [];
  let stores = listBusinessStores(parsed);
  if (stores.length === 0) {
    stores = [
      {
        id: DEFAULT_STORE_ID,
        name: 'Principal',
        isDefault: true,
        active: true
      }
    ];
    parsed.stores = stores.map((s) => ({ ...s }));
  }
  const defaultId =
    (parsed.defaultStoreId != null && String(parsed.defaultStoreId).trim()) ||
    stores.find((s) => s.isDefault)?.id ||
    stores[0].id;
  parsed.defaultStoreId = defaultId;
  // Keep stores array canonical
  if (!Array.isArray(parsed.stores) || parsed.stores.length === 0) {
    parsed.stores = stores.map((s) => ({
      id: s.id,
      name: s.name,
      isDefault: s.id === defaultId,
      active: s.active !== false
    }));
  }
  return listBusinessStores(parsed);
}

/**
 * Resolve active store for writes (movement / receive / transfer).
 * @param {object} [parsed]
 * @param {{ storeId?: string|null }} [opts]
 */
export function resolveActiveStoreId(parsed, opts = {}) {
  if (opts.storeId != null && String(opts.storeId).trim()) {
    return String(opts.storeId).trim();
  }
  ensureBusinessStores(parsed || {});
  if (parsed?.defaultStoreId != null && String(parsed.defaultStoreId).trim()) {
    return String(parsed.defaultStoreId).trim();
  }
  const stores = listBusinessStores(parsed);
  return stores[0]?.id || DEFAULT_STORE_ID;
}

/**
 * Upsert a store definition (name only if id exists).
 * @param {object} parsed
 * @param {{ id: string, name?: string, isDefault?: boolean, active?: boolean }} store
 */
export function upsertBusinessStore(parsed, store = {}) {
  ensureBusinessStores(parsed);
  const id = String(store.id || '').trim();
  if (!id) throw new Error('store_id_required');
  const list = Array.isArray(parsed.stores) ? [...parsed.stores] : [];
  const idx = list.findIndex((s) => String(s?.id ?? s?.storeId) === id);
  const row = {
    id,
    name: String(store.name || id),
    isDefault: store.isDefault === true,
    active: store.active !== false
  };
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...row };
  } else {
    list.push(row);
  }
  if (row.isDefault) {
    for (const s of list) {
      if (String(s.id) !== id) s.isDefault = false;
    }
    parsed.defaultStoreId = id;
  }
  parsed.stores = list;
  return listBusinessStores(parsed);
}

/**
 * Lot belongs to store? Missing storeId on lot → default store (legacy).
 */
export function lotBelongsToStore(lot, storeId, defaultStoreId = DEFAULT_STORE_ID) {
  if (!storeId) return true;
  const lotStore = lot?.storeId != null ? String(lot.storeId).trim() : '';
  const want = String(storeId);
  if (!lotStore) return want === String(defaultStoreId) || want === DEFAULT_STORE_ID;
  return lotStore === want;
}

/**
 * Filter lots for a store view (legacy lots without storeId count toward default).
 * @param {Array|object|null|undefined} lots
 * @param {string|null} storeId — null/'all' → no filter
 * @param {string} [defaultStoreId]
 */
export function filterLotsForStore(lots, storeId, defaultStoreId = DEFAULT_STORE_ID) {
  const list = Array.isArray(lots)
    ? lots
    : lots && typeof lots === 'object'
      ? Object.values(lots)
      : [];
  if (!storeId || storeId === 'all' || String(storeId).trim() === '') {
    return list.filter(Boolean);
  }
  const want = String(storeId).trim();
  return list.filter((lot) => lot && lotBelongsToStore(lot, want, defaultStoreId));
}
